if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      subscriptionFormattingTimers = [];
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.initializeMobileRestockScroll();
        this.initializeSubscriptionFormatting();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initializeMobileRestockScroll() {
        const purchaseBar = this.querySelector('.product__mobile-purchase-bar');
        if (!purchaseBar) return;

        purchaseBar.addEventListener(
          'pointerdown',
          (event) => {
            if (!window.matchMedia('(max-width: 749px)').matches) return;

            const trigger = event.target.closest('[data-restock-scroll-target]');
            if (!trigger || !purchaseBar.contains(trigger)) return;

            const target = document.getElementById(trigger.dataset.restockScrollTarget);
            if (!target) return;

            event.preventDefault();
            event.stopPropagation();

            const header = document.querySelector('.section-header');
            const offset = (header?.getBoundingClientRect().height || 0) + 16;
            const top = window.scrollY + target.getBoundingClientRect().top - offset;

            window.scrollTo({ top, behavior: 'smooth' });
            window.setTimeout(() => target.querySelector('input[name="email"], input[type="email"]')?.focus(), 450);
          },
          true
        );
      }

      initializeSubscriptionFormatting() {
        this.scheduleSubscriptionFormatting();
      }

      scheduleSubscriptionFormatting() {
        this.subscriptionFormattingTimers.forEach((timer) => window.clearTimeout(timer));
        this.subscriptionFormattingTimers = [0, 150, 400, 900].map((delay) =>
          window.setTimeout(() => this.updateSubscriptionFormatting(), delay)
        );
      }

      updateSubscriptionFormatting() {
        const formatSubscriptionPrice = (value) => value.replace(/\s*BRL\s*$/i, '').trim();

        this.querySelectorAll('.shopify_subscriptions_fieldset .in_widget_price').forEach((price) => {
          const formattedPrice = formatSubscriptionPrice(price.textContent);
          if (formattedPrice !== price.textContent.trim()) price.textContent = formattedPrice;
        });

        this.querySelectorAll('.shopify_subscriptions_fieldset .title_and_price_wrapper').forEach((wrapper) => {
          Array.from(wrapper.children).forEach((child) => {
            if (child.querySelector('*')) return;

            const formattedPrice = formatSubscriptionPrice(child.textContent);
            if (formattedPrice !== child.textContent.trim()) child.textContent = formattedPrice;
          });
        });

        this.enhanceSubscriptionWidget();
      }

      enhanceSubscriptionWidget() {
        const fieldset = this.querySelector('.shopify_subscriptions_fieldset');
        if (!fieldset) return;

        this.translateSubscriptionLabels(fieldset);

        const subscriptionGroup = Array.from(fieldset.querySelectorAll('.subscription_group')).find((group) =>
          group.querySelector('select')
        );
        const select = subscriptionGroup?.querySelector('select');

        fieldset.querySelectorAll('.subscription_group').forEach((group) => {
          if (group.dataset.subscriptionClickBound === 'true') return;

          group.dataset.subscriptionClickBound = 'true';
          group.addEventListener('click', (event) => {
            if (event.target.closest('select, option, a, button, input, label')) return;

            const radio = group.querySelector('input[type="radio"]');
            const titleRow = group.querySelector('.title_and_price_wrapper');

            if (!radio || !titleRow || !titleRow.contains(event.target)) return;
            if (radio.checked) return;

            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        if (subscriptionGroup && select) {
          let deliveryNote = subscriptionGroup.querySelector('.theme-subscription-delivery-note');

          if (!deliveryNote) {
            deliveryNote = document.createElement('div');
            deliveryNote.className = 'theme-subscription-delivery-note';
            deliveryNote.innerHTML = '<span aria-hidden="true"></span><span></span>';
            select.before(deliveryNote);
          }

          const updateDeliveryNote = () => {
            const selectedOption = select.selectedOptions[0];
            const label = selectedOption?.textContent.trim() || select.value;
            const text = deliveryNote.querySelector('span:last-child');
            if (text && label) text.textContent = label;
          };

          updateDeliveryNote();
          if (select.dataset.deliveryNoteBound !== 'true') {
            select.dataset.deliveryNoteBound = 'true';
            select.addEventListener('change', () => {
              updateDeliveryNote();
              this.scheduleSubscriptionFormatting();
            });
          }
        }

        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input.dataset.subscriptionFormatBound === 'true') return;

          input.dataset.subscriptionFormatBound = 'true';
          input.addEventListener('change', () => this.scheduleSubscriptionFormatting());
        });

        const policy = fieldset.querySelector('.shopify_subscriptions_app_policy');
        if (!policy) return;

        let policyLink = policy.querySelector('a');
        if (!policyLink) {
          policyLink = document.createElement('a');
          policyLink.href = '#subscription-policy';
          policy.append(policyLink);
        }

        policyLink.classList.add('theme-subscription-policy-link');
        policyLink.innerHTML = `
          <span>Como funciona a assinatura?</span>
          <svg class="theme-subscription-policy-link__icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M0.122605 9.45951C0.091954 9.49854 0.0689655 9.53756 0.0536398 9.57658C0.0383142 9.61561 0.0255428 9.65463 0.0153257 9.69366V9.70537C0.00510856 9.7522 0 9.80293 0 9.85756C0 9.9122 0 9.96683 0 10.0215C0 10.0761 0 10.1307 0 10.1854C0 10.24 0.00510856 10.2907 0.0153257 10.3376V10.3259C0.045977 10.482 0.0945083 10.6263 0.16092 10.759C0.227331 10.8917 0.311622 11.0205 0.413793 11.1454L0.398467 11.1337C0.480204 11.2351 0.569604 11.3307 0.666667 11.4205C0.763729 11.5102 0.868455 11.5902 0.980843 11.6605V11.6722C1.05236 11.7112 1.12133 11.7502 1.18774 11.7893C1.25415 11.8283 1.32822 11.8673 1.40996 11.9063H1.42529C1.48659 11.9376 1.55811 11.961 1.63985 11.9766C1.72158 11.9922 1.80332 12 1.88506 12C1.96679 11.9922 2.04087 11.9805 2.10728 11.9649C2.17369 11.9493 2.23755 11.9259 2.29885 11.8946H2.28352L3.12644 11.5317L5.76245 10.3727L6.02299 10.1971L9.36399 11.6254C9.45594 11.68 9.553 11.7327 9.65517 11.7834C9.75734 11.8341 9.85951 11.8829 9.96169 11.9298L9.99234 11.9415C10.023 11.9493 10.0587 11.9551 10.0996 11.959C10.1405 11.9629 10.1814 11.9649 10.2222 11.9649C10.2631 11.9649 10.304 11.9629 10.3448 11.959C10.3857 11.9551 10.4215 11.9454 10.4521 11.9298V11.9415C10.5338 11.918 10.6079 11.8927 10.6743 11.8654C10.7407 11.838 10.8046 11.8088 10.8659 11.7776H10.8506L11.3103 11.4732C11.3921 11.4029 11.4713 11.3268 11.5479 11.2449C11.6245 11.1629 11.6935 11.079 11.7548 10.9932L11.7701 10.9815C11.811 10.919 11.8493 10.8546 11.8851 10.7883C11.9208 10.722 11.954 10.6537 11.9847 10.5834V10.56C11.9847 10.5366 11.9872 10.5132 11.9923 10.4898C11.9974 10.4663 12 10.4429 12 10.4195C12 10.3961 11.9974 10.3707 11.9923 10.3434C11.9872 10.3161 11.9847 10.2907 11.9847 10.2673V10.279C11.9847 10.2556 11.9872 10.2302 11.9923 10.2029C11.9974 10.1756 12 10.1463 12 10.1151C12 10.0839 11.9974 10.0546 11.9923 10.0273C11.9872 10 11.9847 9.97073 11.9847 9.93951V9.95122C11.9949 9.9278 12 9.90244 12 9.87512C12 9.84781 12 9.82244 12 9.79902C12 9.77561 12 9.75024 12 9.72293C12 9.69561 11.9949 9.67024 11.9847 9.64683V9.65854C11.9745 9.5961 11.954 9.54146 11.9234 9.49463C11.8927 9.4478 11.8519 9.40098 11.8008 9.35415V9.36585C11.7497 9.31122 11.6909 9.26244 11.6245 9.21951C11.5581 9.17659 11.4891 9.13561 11.4176 9.09659H11.4023L9.21073 8.10146L7.29502 7.27024L6.3908 6.86049H5.96169C5.92082 6.85268 5.87739 6.84683 5.83142 6.84293C5.78544 6.83902 5.74202 6.83707 5.70115 6.83707C5.69093 6.83707 5.67816 6.83707 5.66284 6.83707C5.64751 6.83707 5.63474 6.83707 5.62452 6.83707L3.12644 8.0078L1.45594 8.78049C1.45594 8.78049 1.3129 8.83317 1.02682 8.93854C0.740741 9.0439 0.572158 9.1161 0.521073 9.15512C0.45977 9.18634 0.403576 9.21756 0.35249 9.24878C0.301405 9.28 0.250319 9.31512 0.199234 9.35415V9.36585L0.122605 9.45951ZM0.122605 2.7161C0.112388 2.73171 0.10728 2.75122 0.10728 2.77463C0.10728 2.79805 0.10728 2.81756 0.10728 2.83317C0.10728 2.85659 0.10728 2.87805 0.10728 2.89756C0.10728 2.91707 0.112388 2.93854 0.122605 2.96195C0.112388 3.00878 0.104725 3.05756 0.0996169 3.10829C0.0945083 3.15902 0.091954 3.21171 0.091954 3.26634C0.091954 3.32098 0.0945083 3.37366 0.0996169 3.42439C0.104725 3.47512 0.112388 3.5278 0.122605 3.58244V3.57073C0.14304 3.72683 0.186462 3.87317 0.252874 4.00976C0.319285 4.14634 0.403576 4.27317 0.505747 4.39024C0.587484 4.49171 0.676884 4.58537 0.773946 4.67122C0.871009 4.75707 0.975734 4.83512 1.08812 4.90537L1.42529 5.10439C1.48659 5.1278 1.55811 5.14927 1.63985 5.16878C1.72158 5.18829 1.80332 5.19805 1.88506 5.19805C1.96679 5.19805 2.04087 5.18829 2.10728 5.16878C2.17369 5.14927 2.23755 5.1278 2.29885 5.10439H2.28352L3.12644 4.74146L5.76245 3.58244L5.99234 3.46537L9.33333 4.90537C9.42529 4.9522 9.5249 4.99512 9.63218 5.03415C9.73946 5.07317 9.8493 5.10829 9.96169 5.13951L9.99234 5.15122C10.023 5.15902 10.0587 5.16488 10.0996 5.16878C10.1405 5.17268 10.1814 5.17463 10.2222 5.17463C10.2631 5.17463 10.304 5.17268 10.3448 5.16878C10.3857 5.16488 10.4266 5.15902 10.4674 5.15122H10.4521C10.5236 5.12781 10.5926 5.10439 10.659 5.08098C10.7254 5.05756 10.7893 5.03024 10.8506 4.99902H10.8352C10.9272 4.94439 11.0115 4.88976 11.0881 4.83512C11.1648 4.78049 11.2337 4.72585 11.295 4.67122H11.3103C11.3819 4.59317 11.4559 4.51512 11.5326 4.43707C11.6092 4.35902 11.6782 4.27317 11.7395 4.17951L11.7548 4.1678C11.8059 4.12098 11.8493 4.06829 11.8851 4.00976C11.9208 3.95122 11.954 3.89463 11.9847 3.84V3.82829C11.9847 3.80488 11.9872 3.78146 11.9923 3.75805C11.9974 3.73463 12 3.71122 12 3.6878C12 3.66439 11.9974 3.63902 11.9923 3.61171C11.9872 3.58439 11.9847 3.55902 11.9847 3.53561V3.54732C11.9847 3.5239 11.9872 3.49854 11.9923 3.47122C11.9974 3.4439 12 3.41463 12 3.38341C12 3.36 11.9974 3.33268 11.9923 3.30146C11.9872 3.27024 11.9847 3.24293 11.9847 3.21951V3.23122C11.9949 3.2078 12 3.18244 12 3.15512C12 3.1278 12 3.09854 12 3.06732C12 3.0439 12 3.01854 12 2.99122C12 2.9639 11.9949 2.93854 11.9847 2.91512C11.9745 2.86049 11.954 2.80976 11.9234 2.76293C11.8927 2.7161 11.857 2.66927 11.8161 2.62244C11.7548 2.57561 11.6909 2.53073 11.6245 2.4878C11.5581 2.44488 11.4891 2.4039 11.4176 2.36488L11.4023 2.35317L9.24138 1.2761L7.38697 0.503415L6.48276 0.105366L6.29885 0H6.03831C5.96679 0 5.89527 0.00780488 5.82375 0.0234146C5.75223 0.0390244 5.68582 0.062439 5.62452 0.0936585L1.40996 2.04878L0.521073 2.4C0.45977 2.42341 0.403576 2.44683 0.35249 2.47024C0.301405 2.49366 0.255428 2.52098 0.214559 2.5522L0.122605 2.7161Z" fill="#E47C21"/>
          </svg>
        `;
        if (policyLink.dataset.modalBound === 'true') return;
        policyLink.dataset.modalBound = 'true';
        policyLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.openSubscriptionPolicyModal(policyLink.href);
        });
      }

      translateSubscriptionLabels(fieldset) {
        const translateLabel = (label) => {
          const normalizedLabel = label.trim();

          const monthMatch = normalizedLabel.match(/^Deliver every (\d+) months?,\s*(.+?)\s*off$/i);
          if (monthMatch) {
            const interval = monthMatch[1] === '1' ? 'mês' : 'meses';
            return `Receba a cada ${monthMatch[1]} ${interval}, ${monthMatch[2]} de desconto`;
          }

          const monthSingularMatch = normalizedLabel.match(/^Deliver every month,\s*(.+?)\s*off$/i);
          if (monthSingularMatch) {
            return `Receba todos os meses, ${monthSingularMatch[1]} de desconto`;
          }

          const yearMatch = normalizedLabel.match(/^Deliver every year,\s*(.+?)\s*off$/i);
          if (yearMatch) {
            return `Receba anualmente, ${yearMatch[1]} de desconto`;
          }

          return normalizedLabel;
        };

        const walker = document.createTreeWalker(fieldset, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        let node = walker.nextNode();

        while (node) {
          textNodes.push(node);
          node = walker.nextNode();
        }

        textNodes.forEach((textNode) => {
          const originalLabel = textNode.nodeValue.trim();
          const translatedLabel = translateLabel(originalLabel);

          if (translatedLabel !== originalLabel) {
            textNode.nodeValue = textNode.nodeValue.replace(originalLabel, translatedLabel);
          }
        });
      }

      openSubscriptionPolicyModal(policyUrl) {
        let modal = document.querySelector('[data-subscription-policy-modal]');

        if (!modal) {
          const modalItems = [
            {
              number: '1',
              title: 'Como funciona',
              description:
                'Escolha uma frequência e receba o produto automaticamente. Você pode pausar, alterar ou cancelar a qualquer momento.',
            },
            {
              number: '2',
              title: 'Frequência',
              description: 'A cada 15, 30 ou 60 dias. Você recebe um lembrete antes de cada envio.',
            },
            {
              number: '3',
              title: 'Cobrança',
              description:
                'A cobrança ocorre no cartão cadastrado na data de envio. Você pode alterar o método de pagamento a qualquer momento.',
            },
            {
              number: '4',
              title: 'Desconto',
              description:
                'Você economiza 10% em relação à compra única, sempre com a mesma qualidade e entrega.',
            },
            {
              number: '5',
              title: 'Pausa',
              description:
                'Pause a assinatura por um ciclo se precisar. Quando reativar, o próximo envio segue normalmente.',
            },
            {
              number: '6',
              title: 'Alteração',
              description:
                'Altere peso, frequência ou método de pagamento antes do próximo envio.',
            },
            {
              number: '7',
              title: 'Cancelamento',
              description: 'Cancelar é rápido e sem penalidade. Você pode voltar a assinar quando quiser.',
            },
          ];

          modal = document.createElement('dialog');
          modal.dataset.subscriptionPolicyModal = 'true';
          modal.className = 'theme-subscription-modal';
          modal.innerHTML = `
            <div class="theme-subscription-modal__header">
              <h2>Como funciona a assinatura?</h2>
              <button class="theme-subscription-modal__close" type="button" aria-label="Fechar">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <rect width="36" height="36" rx="18" fill="#F6F6F6"/>
                  <path d="M22.5 13.5L13.5 22.5M13.5 13.5L22.5 22.5" stroke="black" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="theme-subscription-modal__divider"></div>
            <div class="theme-subscription-modal__items">
              ${modalItems
                .map(
                  (item) => `
                    <div class="theme-subscription-modal__item">
                      <div class="theme-subscription-modal__item-number">${item.number}</div>
                      <div class="theme-subscription-modal__item-content">
                        <h3>${item.title}</h3>
                        <p>${item.description}</p>
                      </div>
                    </div>
                  `
                )
                .join('')}
            </div>
          `;
          document.body.append(modal);
          modal.querySelector('.theme-subscription-modal__close').addEventListener('click', () => modal.close());
          modal.addEventListener('click', (event) => {
            if (event.target === modal) modal.close();
          });
        }

        if (!modal.open) modal.showModal();
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.subscriptionFormattingTimers.forEach((timer) => window.clearTimeout(timer));
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
        this.scheduleSubscriptionFormatting();
      }

      resetProductFormState() {
        this.classList.add('is-variant-loading');
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            this.classList.remove('is-variant-loading');
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              this.classList.remove('is-variant-loading');
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('MobilePurchaseSummary');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'MobilePurchaseSummary', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallerySliderSource = this.querySelector('media-gallery slider-component');
        const mediaGallerySliderDestination = html.querySelector('media-gallery slider-component');
        const galleryDiscountBadgeSource = mediaGallerySliderSource?.querySelector('.product__gallery-badge--discount');
        const galleryDiscountBadgeDestination = mediaGallerySliderDestination?.querySelector(
          '.product__gallery-badge--discount'
        );
        const galleryCountBadgeSource = mediaGallerySliderSource?.querySelector('.product__gallery-badge--count');
        const galleryCountBadgeDestination = mediaGallerySliderDestination?.querySelector('.product__gallery-badge--count');

        if (mediaGallerySliderSource) {
          if (galleryDiscountBadgeDestination) {
            if (galleryDiscountBadgeSource) {
              galleryDiscountBadgeSource.replaceWith(galleryDiscountBadgeDestination.cloneNode(true));
            } else {
              mediaGallerySliderSource.prepend(galleryDiscountBadgeDestination.cloneNode(true));
            }
          } else {
            galleryDiscountBadgeSource?.remove();
          }

          if (galleryCountBadgeDestination) {
            if (galleryCountBadgeSource) {
              galleryCountBadgeSource.replaceWith(galleryCountBadgeDestination.cloneNode(true));
            } else {
              mediaGallerySliderSource.prepend(galleryCountBadgeDestination.cloneNode(true));
            }
          } else {
            galleryCountBadgeSource?.remove();
          }
        }

        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
